import { useState, useEffect } from "react";
import { number, oneOfType, string, func, arrayOf } from "prop-types";
import CircleInfoSolidV6 from "@shared/v2/Icomoon/Icons/CircleInfoSolidV6";
import Tooltip from "@shared/v2/Tooltip";
import Button from "@shared/v2/Button/Button";
import Modal from "@shared/v2/Modal";
import TextToggle from "@shared/v2/TextToggle/TextToggle";
import { caseTransformingAxios as axios } from "@shared/v2/caseTransformingAxios";
import CreateReports from "./CreateReports";
import NoMLSModal from "./NoMLSModal";
import SpinnerModal from "./SpinnerModal";
import UpdateFrequency from "./UpdateFrequency";
import UpdateOrCreateConfirmModal from "./UpdateOrCreateConfirmModal";

const SEARCH_RADIUS = [
  { label: "Smart Radius", value: "0" },
  { label: "0.5 mile radius", value: "0.5" },
  { label: "1.0 mile radius", value: "1.0" },
  { label: "1.5 mile radius", value: "1.5" },
  { label: "2.0 mile radius", value: "2.0" },
  { label: "3.0 mile radius", value: "3.0" },
  { label: "4.0 mile radius", value: "4.0" },
  { label: "5.0 mile radius", value: "5.0" },
  { label: "10.0 mile radius", value: "10.0" },
  { label: "25.0 mile radius", value: "25.0" },
  { label: "50.0 mile radius", value: "50.0" },
  { label: "100.0 mile radius", value: "100.0" },
];

const DRIP_FREQUENCY = [
  { label: "Never", value: "never" },
  { label: "Every week", value: "every_week" },
  { label: "Every 2 weeks", value: "every_2_weeks" },
  { label: "Every Month", value: "every_month" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Annually", value: "annually" },
];

const CONTACT_ADDRESS_TYPES = [
  { label: "Home", value: "home" },
  { label: "Work", value: "work" },
  { label: "Mailing", value: "mailing" },
  { label: "Investment", value: "investment" },
];

const TOGGLE_OPTIONS = [
  {
    label: "Create Reports",
    value: "create",
  },
  {
    label: "Update Frequency",
    value: "change",
  },
];

const BulkReportsOptionsModal = ({
  closeOptionsModal,
  showStatusModal,
  marketReportCount,
  homeValueReportCount,
  validReportCount,
  selectedLeads,
  country,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isOpenUpdateModal, setIsOpenUpdateModal] = useState(false);
  const [isOpenCreateConfirmModal, setIsOpenCreateConfirmModal] = useState(false);
  const [primaryAgent, setPrimaryAgent] = useState("Primary Agent");
  const [selectedDripFrequency, setSelectedDripFrequency] = useState("every_2_weeks");
  const [selectedContactAddress, setSelectedContactAddress] = useState("home");
  const [selectedRadio, setSelectedRadio] = useState("create");
  const [selectedMls, setSelectedMls] = useState("");
  const [selectedPropertyType, setSelectedPropertyType] = useState("");
  const [propertyTypes, setPropertyTypes] = useState([]);
  const [selectedSearchRadius, setSelectedSearchRadius] = useState("0");
  const [hasApprovedMls, setHasApprovedMls] = useState(false);
  const [isToggleON, setIsToggleON] = useState(false);

  useEffect(() => {
    getAllMlsPropertyTypesForUser();
    getReportSender();
  }, []);

  const handlePropertyTypeChange = (option) => {
    setSelectedPropertyType(option.value || "");
  };

  const handleSearchRadiusChange = (option) => {
    setSelectedSearchRadius(option.value || "");
  };

  const handleDripFrequencyChange = (option) => {
    setSelectedDripFrequency(option.value || "");
  };

  const handleContactAddressChange = (option) => {
    setSelectedContactAddress(option.value || "");
  };

  const handleHVToggleChange = () => {
    setIsToggleON(!isToggleON);
  };

  const radioGroupHandler = (e) => {
    setSelectedRadio(e.target.value);
  };

  const handleClickUpdate = () => {
    setIsOpenUpdateModal(true);
  };

  const handleCloseUpdate = () => {
    setIsOpenUpdateModal(false);
  };

  const handleClickCreate = () => {
    setIsOpenCreateConfirmModal(true);
  };

  const handleCloseCreate = () => {
    setIsOpenCreateConfirmModal(false);
  };

  const getAllMlsPropertyTypesForUser = () => {
    let ids = Brivity.PeoplePage.grid().multiSelect().getSelectedObjectIds();
    axios.get("/bulk_market_reports/all_mls_property_types", { params: { people_ids: ids } }).then((res) => {
      const { propertyTypes: types = [], hasApprovedMls: approved = false } = res.data;

      if (types.length > 0) {
        let defaultValue = types[0].value || "";
        for (const pt of types) {
          if (pt.value.toLowerCase().startsWith("detached")) {
            defaultValue = pt.value;
            break;
          } else if (pt.value.toLowerCase().startsWith("single")) {
            defaultValue = pt.value;
            break;
          } else if (pt.value.toLowerCase().startsWith("resi")) {
            defaultValue = pt.value;
            break;
          }
        }
        setPropertyTypes(types);
        setSelectedPropertyType(defaultValue);
      }

      setHasApprovedMls(approved);
      setIsLoading(false);
    });
  };

  const getReportSender = () => {
    axios.get("/bulk_market_reports/report_sender").then((res) => {
      setPrimaryAgent(res.data?.reportSender || "Primary Agent");
    });
  };

  const saveDialog = () => {
    const data = {
      mls_data_source_id: selectedMls,
      property_type: selectedPropertyType,
      radius: selectedSearchRadius,
      recurring_frequency: selectedDripFrequency,
      contact_address_type: selectedContactAddress,
      include_home_value: isToggleON,
    };

    if (isOpenUpdateModal) {
      setIsOpenUpdateModal(false);
    }
    if (isOpenCreateConfirmModal) {
      setIsOpenCreateConfirmModal(false);
    }

    showStatusModal(data, selectedRadio);
  };

  return (
    <>
      {isLoading ? (
        <SpinnerModal closeOptionsModal={closeOptionsModal} />
      ) : (
        <>
          {hasApprovedMls ? (
            <Modal
              className="tw-flex tw-justify-center tw-items-center"
              dialogClassName="!tw-w-[650px] tw-h-auto"
              contentClassName="tw-w-full tw-h-full"
              backdrop="static"
              show
              onHide={closeOptionsModal}
            >
              <Modal.Header
                titleClassName="tw-mb-12px tw-text-gray-75"
                title="Market Report Actions"
                closeButton
              />

              <Modal.Body className="tw-mb-[32px] ">
                <p className="tw-pb-[22px] tw-text-[14px] tw-font-normal tw-leading-[20px] tw-text-gray-50">
                  Market Reports are one of the best ways to nurture seller leads and your sphere by showing
                  them what's going on in the real estate market in their neighborhood.
                </p>
                <div className="tw-mx-[-10px]">
                  <div className="tw-px-[10px]">
                    <TextToggle
                      className="tw-w-full"
                      labelContainerClassName='tw-flex-1'
                      options={TOGGLE_OPTIONS}
                      value={selectedRadio}
                      size="medium"
                      onChange={radioGroupHandler}
                    />
                  </div>
                </div>
                {selectedRadio === "create" && (
                  <CreateReports
                    country={country}
                    selectedLeads={selectedLeads}
                    marketReportCount={marketReportCount}
                    homeValueReportCount={homeValueReportCount}
                    handlePropertyTypeChange={handlePropertyTypeChange}
                    propertyTypes={propertyTypes}
                    selectedPropertyType={selectedPropertyType}
                    handleDripFrequencyChange={handleDripFrequencyChange}
                    handleSearchRadiusChange={handleSearchRadiusChange}
                    handleContactAddressChange={handleContactAddressChange}
                    ContactAddresses={CONTACT_ADDRESS_TYPES}
                    searchRadius={SEARCH_RADIUS}
                    selectedSearchRadius={selectedSearchRadius}
                    selectedContactAddress={selectedContactAddress}
                    dripFrequency={DRIP_FREQUENCY}
                    selectedDripFrequency={selectedDripFrequency}
                    isToggleON={isToggleON}
                    handleHVToggleChange={handleHVToggleChange}
                  />
                )}
                {selectedRadio === "change" && (
                  <UpdateFrequency
                    validReportCount={validReportCount}
                    selectedLeads={selectedLeads}
                    handleDripFrequencyChange={handleDripFrequencyChange}
                    dripFrequency={DRIP_FREQUENCY}
                    selectedDripFrequency={selectedDripFrequency}
                  />
                )}
              </Modal.Body>
              <Modal.Footer className="tw-flex tw-flex-row tw-justify-between ">
                <Button size="medium" schema="tertiary" onClick={closeOptionsModal}>
                  Cancel
                </Button>
                {selectedRadio === "create" && (
                  <>
                    <span className="tw-pt-[9px] tw-text-[12px] tw-font-normal tw-leading-[16px] tw-text-gray-75">
                      Reports will be sent out from {primaryAgent === "Primary Agent" ? "the " : ""}
                      <span className="tw-font-bold">{primaryAgent}</span>
                      {primaryAgent !== "Primary Agent" && (
                        <Tooltip
                          trigger={<CircleInfoSolidV6 size="m" className="tw-text-gray-75 tw-ml-[6px]" />}
                          innerClassName="!tw-text-left tw-max-w-[180px]"
                          content={
                            <>
                              <div>
                                We recommend sending out Market Reports from the contact's{" "}
                                <span className="tw-font-bold">Primary Agent</span> instead of a specific team
                                member.
                              </div>
                              <div className="tw-pt-[9px]">
                                This can be updated from Brivity CMA. Reach out to support if you need help.
                              </div>
                            </>
                          }
                          placement="top"
                        />
                      )}
                    </span>
                  </>
                )}

                <Button
                  size="medium"
                  disabled={
                    (selectedRadio === "create" && marketReportCount < 1) ||
                    (selectedRadio === "change" && validReportCount < 1)
                  }
                  schema="primary"
                  onClick={selectedRadio === "create" ? handleClickCreate : handleClickUpdate}
                >
                  {selectedRadio === "create" ? "create" : "update"}
                </Button>
              </Modal.Footer>
            </Modal>
          ) : (
            <NoMLSModal closeOptionsModal={closeOptionsModal} />
          )}
        </>
      )}
      {isOpenUpdateModal && (
        <UpdateOrCreateConfirmModal closeOptionsModal={handleCloseUpdate} saveDialog={saveDialog} />
      )}
      {isOpenCreateConfirmModal && (
        <UpdateOrCreateConfirmModal
          isCreate
          closeOptionsModal={handleCloseCreate}
          saveDialog={saveDialog}
          count={marketReportCount}
        />
      )}
    </>
  );
};

BulkReportsOptionsModal.propTypes = {
  closeOptionsModal: func,
  showStatusModal: func,
  country: string,
  marketReportCount: oneOfType([number, string]),
  homeValueReportCount: oneOfType([number, string]),
  validReportCount: oneOfType([number, string]),
  selectedLeads: arrayOf(oneOfType([number, string])),
};

BulkReportsOptionsModal.defaultProps = {
  closeOptionsModal: null,
  showStatusModal: null,
  marketReportCount: 0,
  homeValueReportCount: 0,
  validReportCount: 0,
  selectedLeads: [],
  country: "",
};
export default BulkReportsOptionsModal;
